/**
 * Signin — tenant-grouped demo sign-in (workforce spec 2026-07-16 §3):
 * pick an organization (bank / university / retail estate…), then a persona
 * within it. Personas map to real role-scoped sessions via demoSignIn →
 * whoami (which now carries per-process `assignments`).
 *
 * Data: getDemoTenants (declared op — GET /demo/tenants). Until it ships, the
 * screen falls back to the existing flat GET /demo/personas chooser, so
 * nothing regresses. Works phone → desktop.
 */
import { useEffect, useState } from "react";
import { Icon, T } from "@v0y4ge/jrni-ui";
import { fetchPersonas, signIn, landingRouteForRole, initialsOf, colorFor, getActiveOrganizationId, getUser, type Persona } from "./access";
import { navProfileForUserAsync } from "./navProfiles";
import { getDemoTenants, type DemoTenant, type DemoPersonaDto } from "../screens/workforce/wf-api";
import { useNav } from "./router";
import { useViewport } from "./useResponsive";
import { availableLocales, currentLocale, localeDisplayName, refreshTranslationCatalog, setAppLocale, useT, t as i18nT } from "../lib/i18n";

const SANS = "var(--jrni-font-family-sans)";
const ASSET_BASE = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
// Softer brand: lavender wash, not a slab of dark purple.
const WASH = "linear-gradient(160deg, #F4F2FC 0%, #EEF5F3 100%)";

const INDUSTRY_LABEL: Record<string, string> = { events: "Events", banking: "Banking", "higher-ed": "Higher education", retail: "Retail" }; // ui-config

type SigninPersona = Persona & { locale?: string; assignments?: Partial<Record<string, string>> };
// Process-role chips (2026-08-10): personas carry a role PER PROCESS in
// `assignments` (seed-world/<tenant>/personas.json), but the card only ever
// showed the primary `role` label — so a Branch Manager who is also the tenant's
// Event Manager read as having no events access at all. Chips make the full
// cross-process assignment legible at the chooser. Scope pins (regionId/branchId)
// are not roles and are filtered out. ui-config.
const PROCESS_LABEL: Record<string, string> = { events: "Events", workforce: "Workforce", queues: "Queues", storeops: "Store ops", appointments: "Appointments" };
function assignmentChips(a?: Partial<Record<string, string>>): { process: string; role: string }[] {
  if (!a) return [];
  return Object.keys(PROCESS_LABEL)
    .filter((k) => typeof a[k] === "string" && a[k])
    .map((k) => ({ process: PROCESS_LABEL[k], role: a[k] as string }));
}
// Locale → representative flag region for the chooser pill (demo affordance;
// bare tags per the kit's demo-personas fixture). An explicit region subtag
// wins (de-CH→CH, zh-HK→HK). ui-config.
const LOCALE_REGION: Record<string, string> = { en: "US", fr: "FR", de: "DE", hi: "IN", ja: "JP", es: "ES", ko: "KR", ar: "SA", it: "IT", pt: "BR", zh: "HK" };
function flagOf(tag: string): string {
  const [lang, region] = tag.split("-");
  const r = (region && region.length === 2 ? region : LOCALE_REGION[lang]) || "US";
  return String.fromCodePoint(...[...r.toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function toPersona(d: DemoPersonaDto): SigninPersona {
  const name = d.displayName ?? d.name ?? d.email ?? "";
  return { id: d.id, name, email: d.email ?? "", role: d.role ?? "", init: initialsOf(name), color: colorFor(d.id ?? name), blurb: d.blurb ?? "", locale: d.locale ?? "en", assignments: d.assignments };
}

function Mark() {
  const t = useT();
  const base = (window as unknown as { __STUDIO_ASSET_BASE__?: string }).__STUDIO_ASSET_BASE__ ?? "";
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
      <img src={`${base}/jrni-logo.png`} alt={t("shell.signin.jrni", "JRNI")} style={{ height: 30, width: "auto", display: "block" }} />
      <span style={{ fontSize: 19, fontWeight: 500, color: T.n2, letterSpacing: -0.3, fontFamily: SANS, borderLeft: `1px solid ${T.border}`, paddingLeft: 10 }}>{t("shell.signin.studio", "Studio")}</span>
    </div>
  );
}

function PersonaCard({ p, orgs, onPick }: { p: SigninPersona; orgs?: number; onPick: (p: SigninPersona) => void }) {
  const t = useT();
  const chips = assignmentChips(p.assignments);
  return (
    <button data-testid="signin-persona-pick" data-id={p.id} onClick={() => onPick(p)}
      style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", borderRadius: 12, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", textAlign: "left", fontFamily: SANS, transition: "border-color 120ms, box-shadow 120ms" }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.boxShadow = T.shadowMd; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
      <span style={{ position: "relative", width: 46, height: 46, borderRadius: 999, background: p.color, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 700, flexShrink: 0, overflow: "hidden" }}>
        {p.init}
        {/* Placeholder portrait (app/public/avatars/<id>.png, generated — swap for real
            headshots file-by-file). Initials sit beneath; onError reveals them. */}
        <img src={`${ASSET_BASE}/avatars/${p.id}.png`} alt="" aria-hidden onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{p.name}</span>
        <span style={{ display: "block", fontSize: 13, fontWeight: 600, color: p.color, margin: "1px 0 3px" }}>{p.role}{orgs && orgs > 1 ? <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 700, color: T.n2, background: T.n6, borderRadius: 999, padding: "2px 8px", verticalAlign: "1px" }}>{orgs} {t("shell.signin.organizations", "organizations")}</span> : null}</span>
        <span style={{ display: "block", fontSize: 12.5, color: T.soft, lineHeight: 1.45 }}>{p.blurb}</span>
        {chips.length ? (
          <span style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
            {chips.map((c) => (
              <span key={c.process} style={{ display: "inline-flex", alignItems: "baseline", gap: 4, fontSize: 10.5, lineHeight: 1.5, color: T.n2, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>
                <span style={{ color: T.soft, fontWeight: 600 }}>{c.process}</span>
                <span style={{ fontWeight: 700 }}>{c.role}</span>
              </span>
            ))}
          </span>
        ) : null}
      </span>
      {p.locale ? (
        <span title={localeDisplayName(p.locale)} aria-label={localeDisplayName(p.locale)} style={{ display: "inline-flex", alignItems: "center", gap: 5, flexShrink: 0, fontSize: 10.5, fontWeight: 700, color: T.n2, background: T.n6, borderRadius: 999, padding: "3px 8px" }}>
          <span style={{ fontSize: 13, lineHeight: 1 }} aria-hidden>{flagOf(p.locale)}</span>{p.locale.toUpperCase()}
        </span>
      ) : null}
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke={T.n3} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden><path d="m6 4 4 4-4 4" /></svg>
    </button>
  );
}

export default function Signin() {
  const t = useT();
  const translate = t;
  const nav = useNav();
  const vp = useViewport();
  const [tenants, setTenants] = useState<DemoTenant[] | null>(null);   // null = loading / unavailable
  const [flat, setFlat] = useState<Persona[]>([]);                     // fallback: flat persona list
  const [tenantsFailed, setTenantsFailed] = useState(false);
  const [active, setActive] = useState<DemoTenant | null>(null);       // ui-local (chooser step)
  const [signing, setSigning] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    getDemoTenants()
      .then((r) => { if (live && Array.isArray(r?.tenants) && r.tenants.length) setTenants(r.tenants); else if (live) setTenantsFailed(true); })
      .catch(() => { if (live) setTenantsFailed(true); });
    return () => { live = false; };
  }, []);
  useEffect(() => {
    if (!tenantsFailed) return;
    let live = true;
    fetchPersonas().then((ps) => { if (live) setFlat(ps); });
    return () => { live = false; };
  }, [tenantsFailed]);

  const pick = async (p: SigninPersona) => {
    if (signing) return;
    setSigning(p.id);
    try {
      await signIn(p.id);
      // Persona-locale-session parity (kit demo semantics): the persona's saved
      // language preference (their user record) applies at sign-in — it's what
      // the chooser's flag pill promises. ui-local render config; in-session
      // changes + persistence live on Account details → Profile → Language.
      const tag = p.locale ?? "en";
      if (availableLocales().includes(tag) && tag !== currentLocale()) {
        setAppLocale(tag);
        void refreshTranslationCatalog(getActiveOrganizationId());
      }
      // Overnight docket (2026-07-28): a served nav profile's landing route
      // (getNavComposition) takes precedence when one resolves for this user;
      // landingRouteForRole is the pre-profile fallback.
      const profile = await navProfileForUserAsync(getUser());
      nav.reset(profile?.landing || landingRouteForRole(p.role));
    } finally { setSigning(null); }
  };

  const grouped = tenants && !tenantsFailed;
  const heading = grouped && !active ? "Choose an organization" : "Sign in to continue";
  const sub = grouped && !active
    ? "Each demo organization runs events, appointments and walk-in queues — with people tuned to its industry."
    : "Choose a role to sign in as. You'll see exactly the apps and tools that role is enabled for.";

  return (
    <div style={{ height: "100%", maxHeight: "100vh", background: WASH, display: "flex", flexDirection: "column", alignItems: "center", padding: vp === "phone" ? "0" : 24, fontFamily: SANS, overflowY: "auto" }}>
      {/* margin:auto (not justify-content:center) so centering stays overflow-safe: on short viewports the column top remains reachable and the page scrolls. */}
      <div style={{ width: "100%", maxWidth: vp === "desktop" ? 900 : 460, display: "flex", flexDirection: "column", gap: 0, margin: vp === "phone" ? "0 auto" : "auto" }}>
        {/* Header */}
        <div style={{ padding: vp === "phone" ? "40px 22px 20px" : "8px 8px 26px", textAlign: vp === "phone" ? "left" : "center", display: "flex", flexDirection: "column", alignItems: vp === "phone" ? "flex-start" : "center", gap: 14 }}>
          <Mark />
          <div>
            <h1 style={{ margin: 0, fontSize: vp === "phone" ? 26 : 30, fontWeight: 700, color: T.n1, letterSpacing: -0.5, lineHeight: 1.15 }}>{heading}</h1>
            <p style={{ margin: "8px 0 0", fontSize: 15, color: T.soft, lineHeight: 1.5, maxWidth: 460 }}>{sub}</p>
          </div>
        </div>

        {/* Card panel */}
        <div style={{
          background: vp === "phone" ? "transparent" : "#fff",
          borderRadius: vp === "phone" ? 0 : 16,
          border: vp === "phone" ? "none" : `1px solid ${T.border}`,
          boxShadow: vp === "phone" ? "none" : T.shadowLg,
          padding: vp === "phone" ? "0 14px 28px" : 16,
          display: "flex", flexDirection: "column", gap: 10,
        }}>
          {grouped && active ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "2px 4px 8px" }}>
                <button data-testid="signin-tenant-back" onClick={() => setActive(null)} aria-label={t("shell.signin.backToOrganizations", "Back to organizations")}
                  style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${T.border}`, background: "#fff", color: T.n2, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.arrow dir="left" size={13} /></button>
                <span style={{ width: 34, height: 34, borderRadius: 9, background: active.brandColor || T.p1, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 14, flexShrink: 0 }}>{active.initials || initialsOf(active.name)}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: T.n1 }}>{active.name}</span>
                  <span style={{ display: "block", fontSize: 12, color: T.soft }}>{INDUSTRY_LABEL[active.industry] ?? active.industry}</span>
                </span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr" : "1fr", gap: 10 }}>
                {active.personas.map((d) => <PersonaCard key={d.id} p={toPersona(d)} orgs={d.workspaces ? d.workspaces.length : undefined} onPick={pick} />)}
              </div>
            </>
          ) : grouped ? (
            <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr 1fr" : "1fr", gap: 10 }}>
              {(tenants ?? []).map((t) => (
                <button key={t.id} data-testid="signin-tenant-pick" data-id={t.id} onClick={() => setActive(t)}
                  style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 10, padding: "18px 16px", borderRadius: 12, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", textAlign: "left", fontFamily: SANS, transition: "border-color 120ms, box-shadow 120ms" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = t.brandColor || T.p1; e.currentTarget.style.boxShadow = T.shadowMd; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.boxShadow = "none"; }}>
                  <span style={{ width: 44, height: 44, borderRadius: 11, background: t.brandColor || T.p1, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16 }}>{t.initials || initialsOf(t.name)}</span>
                  <span>
                    <span style={{ display: "block", fontSize: 15.5, fontWeight: 700, color: T.n1 }}>{t.name}</span>
                    <span style={{ display: "block", fontSize: 12, fontWeight: 600, color: t.brandColor || T.p1, margin: "1px 0 4px" }}>{INDUSTRY_LABEL[t.industry] ?? t.industry}</span>
                    <span style={{ display: "block", fontSize: 12.5, color: T.soft, lineHeight: 1.45 }}>{t.blurb}</span>
                  </span>
                  <span style={{ fontSize: 11.5, color: T.soft, display: "inline-flex", alignItems: "center", gap: 5 }}><Icon.users size={12} /> {t.personas.length} {translate("shell.signin.people", "people")}</span>
                </button>
              ))}
            </div>
          ) : tenantsFailed ? (
            <div style={{ display: "grid", gridTemplateColumns: vp === "desktop" ? "1fr 1fr" : "1fr", gap: 10 }}>
              {flat.map((p) => <PersonaCard key={p.id} p={p} onPick={pick} />)}
            </div>
          ) : (
            <div style={{ padding: 30, textAlign: "center", fontSize: 13.5, color: T.soft }}>{t("shell.signin.loadingSignInOptions", "Loading sign-in options…")}</div>
          )}
        </div>

        <p style={{ textAlign: "center", fontSize: 12, color: T.soft, margin: "20px 0 28px", padding: "0 22px" }}>
          {i18nT("shell.signin.demoSignInInProductionThisIsYourEmailAmpPasswordOr.rendered", "Demo sign-in. In production this is your email & password or single sign-on.")}
        </p>
      </div>
    </div>
  );
}
